import React, { useRef } from "react";
import ReactDOM from "react-dom";
import { X, Paperclip, Printer } from "lucide-react";
import { useReactToPrint } from "react-to-print";

const formatDate = (dateString) => {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (isNaN(d)) return dateString;
  return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
};

const DeliveryNoteViewModal = ({
  isOpen,
  onClose,
  poItems = [],
  poLabel = "PO",
  order = {},
  managerApproval = {},
  snapshot = {},
}) => {
  const contentRef = useRef(null);
  const handlePrint = useReactToPrint({ contentRef });

  if (!isOpen) return null;

  const { rows = [], processedBy = "", remarks = "", attachment = null } = snapshot;

  return ReactDOM.createPortal(
    <div className="edn-modal-overlay" onClick={onClose}>
      <div className="edn-modal-container" onClick={(e) => e.stopPropagation()}>

        <div className="edn-modal-header">
          <span className="edn-modal-header-title">Delivery Note</span>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              className="process-create-btn"
              onClick={handlePrint}
              type="button"
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              <Printer size={16} />
              Print / Save as PDF
            </button>
            <button className="edn-modal-close-btn" onClick={onClose} type="button" aria-label="Close">
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="edn-modal-body">

          <div className="edn-doc-preview" ref={contentRef}>
            <p className="edn-doc-title">DELIVERY NOTE</p>

            <div className="edn-doc-meta">
              <div className="edn-doc-meta-row">
                <div className="edn-doc-meta-cell">
                  <span className="edn-doc-meta-label">Order Type:</span>
                  <span className="edn-doc-meta-value">{order.orderType ?? "Regular Order"}</span>
                </div>
                <div className="edn-doc-meta-cell">
                  <span className="edn-doc-meta-label">Date Issued:</span>
                  <span className="edn-doc-meta-value">{formatDate(new Date().toISOString())}</span>
                </div>
              </div>
              <div className="edn-doc-meta-row">
                <div className="edn-doc-meta-cell">
                  <span className="edn-doc-meta-label">Merchant Name:</span>
                  <span className="edn-doc-meta-value">{order.merchant?.name ?? order.merchantName ?? "-"}</span>
                </div>
                <div className="edn-doc-meta-cell">
                  <span className="edn-doc-meta-label">Store Code:</span>
                  <span className="edn-doc-meta-value">{order.facility?.code ?? order.facilityCode ?? "-"}</span>
                </div>
              </div>
              <div className="edn-doc-meta-row">
                <div className="edn-doc-meta-cell">
                  <span className="edn-doc-meta-label">Location:</span>
                  <span className="edn-doc-meta-value">{order.facility?.name ?? order.facilityName ?? "-"}</span>
                </div>
                <div className="edn-doc-meta-cell">
                  <span className="edn-doc-meta-label">PR No.:</span>
                  <span className="edn-doc-meta-value">{managerApproval.prNo ?? "-"}</span>
                </div>
              </div>
            </div>

            <div className="edn-doc-table-wrap">
              <table className="edn-doc-table">
                <thead>
                  <tr className="edn-doc-table-section-row">
                    <th colSpan={5} className="edn-doc-table-section-left">
                      Order Details ({poLabel})
                    </th>
                    <th colSpan={2} className="edn-doc-table-section-right">
                      Delivery Details
                    </th>
                  </tr>
                  <tr className="edn-doc-table-col-header">
                    <th>No.</th>
                    <th>SKU</th>
                    <th>Item Description</th>
                    <th>UOM</th>
                    <th>PO QTY</th>
                    <th>Delivered QTY</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const MIN_ROWS = 10;
                    const dataRows = poItems.map((item, idx) => (
                      <tr
                        key={idx}
                        className={idx % 2 !== 0 ? "edn-doc-table-row--alt" : ""}
                      >
                        <td>{idx + 1}</td>
                        <td>{item.sku ?? item.productSku ?? "-"}</td>
                        <td>{item.description ?? item.productName ?? item.name ?? "-"}</td>
                        <td>{item.uomName ?? item.UomName ?? item.uom ?? "-"}</td>
                        <td>{item.quantity ?? item.qty ?? "-"}</td>
                        <td>{rows[idx]?.deliveredQty || "-"}</td>
                        <td>{rows[idx]?.remarks || "-"}</td>
                      </tr>
                    ));
                    const emptyCount = Math.max(0, MIN_ROWS - poItems.length);
                    const emptyRows = Array.from({ length: emptyCount }, (_, i) => (
                      <tr key={`empty-${i}`} className={(poItems.length + i) % 2 !== 0 ? "edn-doc-table-row--alt" : ""}>
                        <td>&nbsp;</td><td></td><td></td><td></td><td></td><td></td><td></td>
                      </tr>
                    ));
                    return [...dataRows, ...emptyRows];
                  })()}
                </tbody>
              </table>
            </div>

            <div className="edn-doc-sig-grid">
              <div className="edn-doc-sig-cell">
                <span className="edn-doc-sig-label">Processed by</span>
                <span className="edn-doc-sig-value">{processedBy || "Employee Name Here"}</span>
              </div>
              <div className="edn-doc-sig-cell">
                <span className="edn-doc-sig-label">Noted by</span>
                <span className="edn-doc-sig-value">General Manager Name Here</span>
              </div>
              <div className="edn-doc-sig-cell">
                <span className="edn-doc-sig-label">Date Inspected</span>
                <span className="edn-doc-sig-value"></span>
              </div>
              <div className="edn-doc-sig-cell">
                <span className="edn-doc-sig-label">Inspected by</span>
                <span className="edn-doc-sig-value"></span>
              </div>
              <div className="edn-doc-sig-cell">
                <span className="edn-doc-sig-label">Date Received</span>
                <span className="edn-doc-sig-value"></span>
              </div>
              <div className="edn-doc-sig-cell">
                <span className="edn-doc-sig-label">Received by (PDL Merchant)</span>
                <span className="edn-doc-sig-value"></span>
              </div>
            </div>

            <p className="edn-doc-page-footer">PAGE 1 OUT OF X</p>
          </div>

          <div className="edn-form edn-form--spaced">
            <div className="edn-form-row">
              <div className="edn-form-group">
                <label className="edn-form-label">Attachment</label>
                {attachment ? (
                  <div className="edn-attachment-chip">
                    <Paperclip size={14} />
                    <span className="edn-attachment-name" title={attachment.name}>
                      {attachment.name.length > 22
                        ? attachment.name.slice(0, 9) + "…"
                        : attachment.name}
                    </span>
                    <span className="edn-attachment-full">{attachment.name}</span>
                  </div>
                ) : (
                  <span style={{ fontSize: "0.82rem", color: "#9ca3af" }}>No attachment</span>
                )}
              </div>

              <div className="edn-form-group">
                <label className="edn-form-label">Processed by</label>
                <input
                  type="text"
                  className="edn-form-input"
                  value={processedBy}
                  readOnly
                  style={{ background: "#f9fafb", cursor: "not-allowed" }}
                />
              </div>
            </div>

            <div className="edn-form-group">
              <label className="edn-form-label">Remarks</label>
              <textarea
                className="edn-form-textarea"
                rows={3}
                value={remarks}
                readOnly
                style={{ background: "#f9fafb", cursor: "not-allowed" }}
              />
            </div>
          </div>

        </div>

      </div>

    </div>,
    document.body
  );
};

export default DeliveryNoteViewModal;
